/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Card, Row, Col, Space, Flex, Table, Input, Select, Button,
  Typography, message, Drawer, Checkbox, Tooltip, Tag,
} from "antd";
import {
  SearchOutlined, ClearOutlined, FileExcelOutlined,
  SettingOutlined, MenuOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import ReportService from "../../../../service/ERP/Report.service";
import CustomerService from "../../../../service/ERP/Customer.service";
import JobOrderService from "../../../../service/ERP/JobOrder.service";
import { ALL_COLUMNS, SUM_KEYS, DEFAULT_VISIBLE, buildColumns, num, fmt } from "./model";
import "./MyPage.css";

const reportService = ReportService();
const customerService = CustomerService();
const joService = JobOrderService();

/* แถวใน drawer จัดคอลัมน์ — ลากสลับลำดับได้ */
function SortableColumnRow({ id, title, checked, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 20, background: "#fafafa" } : {}),
  };
  return (
    <div ref={setNodeRef} style={style} className="sw-col-row">
      <MenuOutlined className="sw-col-drag" {...attributes} {...listeners} />
      <Checkbox checked={checked} onChange={(e) => onToggle(id, e.target.checked)}>
        {title}
      </Checkbox>
    </div>
  );
}

export default function SizeWeightAccess() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [cusOptions, setCusOptions] = useState([]);
  const [joOptions, setJoOptions] = useState([]);

  const [filter, setFilter] = useState({ cuscode: undefined, jocode: undefined, keyword: "" });

  const [orderedKeys, setOrderedKeys] = useState(ALL_COLUMNS.map((c) => c.key));
  const [visibleKeys, setVisibleKeys] = useState(DEFAULT_VISIBLE);
  const [openConfig, setOpenConfig] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  /* ---- โหลดตัวเลือกตัวกรอง ---- */
  const loadOptions = async () => {
    try {
      const [cusRes, joRes] = await Promise.all([
        customerService.search({}),
        joService.search({}),
      ]);
      setCusOptions(
        (cusRes.data?.data || []).map((c) => ({ value: c.cuscode, label: `${c.cuscode} - ${c.cusname}` }))
      );
      setJoOptions(
        (joRes.data?.data || []).map((j) => ({ value: j.jocode, label: `${j.jocode}${j.cusname ? ` - ${j.cusname}` : ""}` }))
      );
    } catch {
      /* ตัวเลือกโหลดไม่ได้ ไม่ถือว่าเป็น error ร้ายแรง */
    }
  };

  /* ---- ค้นหา ---- */
  const handleSearch = async (override = null) => {
    const f = override ?? filter;
    try {
      setLoading(true);
      const res = await reportService.getSizeWeight(f);
      setRows(res.data?.data || []);
    } catch (err) {
      message.error(err?.response?.data?.message || "ดึงข้อมูลไม่สำเร็จ");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    const empty = { cuscode: undefined, jocode: undefined, keyword: "" };
    setFilter(empty);
    handleSearch(empty);
  };

  useEffect(() => {
    loadOptions();
    handleSearch();
  }, []);

  /* ---- export ตามลำดับคอลัมน์ที่เห็นบนจอ ---- */
  const handleExport = async () => {
    if (rows.length === 0) {
      message.warning("ไม่มีข้อมูลให้ export");
      return;
    }
    try {
      setLoading(true);
      const columns = orderedKeys
        .filter((k) => visibleKeys.includes(k))
        .map((k) => {
          const c = ALL_COLUMNS.find((x) => x.key === k);
          return { key: c.key, title: c.title };
        });
      await reportService.getSizeWeightDownload({ data: rows, columns });
      message.success("Export สำเร็จ");
    } catch {
      message.error("Export ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ---- จัดลำดับคอลัมน์ ---- */
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrderedKeys((prev) => {
      const from = prev.indexOf(active.id);
      const to = prev.indexOf(over.id);
      return arrayMove(prev, from, to);
    });
  };

  const toggleColumn = (key, checked) => {
    setVisibleKeys((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  };

  const columns = useMemo(
    () => buildColumns(orderedKeys, visibleKeys),
    [orderedKeys, visibleKeys]
  );

  /* ---- ยอดรวมท้ายตาราง ---- */
  const totals = useMemo(() => {
    const t = {};
    SUM_KEYS.forEach((k) => {
      t[k] = rows.reduce((s, r) => s + num(r[k]), 0);
    });
    return t;
  }, [rows]);

  const FormSearch = (
    <Card size="small" style={{ backgroundColor: "#f0f0f0" }}>
      <Row gutter={[8, 8]} className="m-0">
        <Col xs={24} sm={12} md={7}>
          <Typography.Text type="secondary">ลูกค้า</Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="ทุกลูกค้า"
            value={filter.cuscode}
            options={cusOptions}
            onChange={(v) => setFilter((s) => ({ ...s, cuscode: v }))}
          />
        </Col>
        <Col xs={24} sm={12} md={7}>
          <Typography.Text type="secondary">
            Job Order <Tooltip title="ใช้ดึงจำนวนลัง (order c/s) มาคำนวณยอดรวม"><span>ⓘ</span></Tooltip>
          </Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="ไม่ระบุ (order c/s = 0)"
            value={filter.jocode}
            options={joOptions}
            onChange={(v) => setFilter((s) => ({ ...s, jocode: v }))}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Typography.Text type="secondary">ค้นหา</Typography.Text>
          <Input
            allowClear
            placeholder="รหัส / ชื่อสินค้า / ลูกค้า"
            value={filter.keyword}
            suffix={<SearchOutlined />}
            onChange={(e) => setFilter((s) => ({ ...s, keyword: e.target.value }))}
            onPressEnter={() => handleSearch()}
          />
        </Col>
        <Col xs={24} md={4}>
          <Typography.Text type="secondary">&nbsp;</Typography.Text>
          <Flex gap={6}>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => handleSearch()}>
              ค้นหา
            </Button>
            <Button danger icon={<ClearOutlined />} onClick={handleClear} />
          </Flex>
        </Col>
      </Row>
    </Card>
  );

  const TitleTable = (
    <Flex className="width-100" align="center" wrap gap={8}>
      <Col flex="auto" className="p-0">
        <Flex align="center" gap={8}>
          <Typography.Title className="m-0 !text-zinc-800" level={4}>
            ขนาด + น้ำหนักของสินค้า
          </Typography.Title>
          <Tag color="blue">{rows.length} รายการ</Tag>
        </Flex>
      </Col>
      <Flex gap={6}>
        <Button size="small" icon={<ReloadOutlined />} onClick={() => handleSearch()}>
          รีเฟรช
        </Button>
        <Button size="small" icon={<SettingOutlined />} onClick={() => setOpenConfig(true)}>
          จัดคอลัมน์
        </Button>
        <Button
          size="small"
          className="bn-success-outline bn-center"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
        >
          Export Excel
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <div className="size-weight-report">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {FormSearch}

        <Card styles={{ body: { paddingTop: 8 } }}>
          <Table
            title={() => TitleTable}
            size="small"
            bordered
            rowKey="key"
            loading={loading}
            columns={columns}
            dataSource={rows}
            scroll={{ x: "max-content", y: 560 }}
            pagination={{
              defaultPageSize: 50,
              pageSizeOptions: [25, 50, 100, 200],
              showSizeChanger: true,
              showTotal: (t, r) => `${r[0]}-${r[1]} จาก ${t} รายการ`,
            }}
            locale={{ emptyText: <span>ไม่พบข้อมูล — ลองปรับตัวกรองด้านบน</span> }}
            summary={() =>
              rows.length > 0 ? (
                <Table.Summary fixed>
                  <Table.Summary.Row className="sw-summary">
                    {columns.map((c, i) => (
                      <Table.Summary.Cell key={c.key} index={i} align={c.align}>
                        {i === 0 ? (
                          <strong>รวม</strong>
                        ) : SUM_KEYS.includes(c.key) ? (
                          <strong>
                            {fmt(
                              totals[c.key],
                              ALL_COLUMNS.find((x) => x.key === c.key)?.digits ?? 2
                            )}
                          </strong>
                        ) : null}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </Card>
      </Space>

      {/* จัดลำดับ / ซ่อน-แสดงคอลัมน์ */}
      <Drawer
        title="จัดคอลัมน์"
        open={openConfig}
        onClose={() => setOpenConfig(false)}
        width={340}
        extra={
          <Button
            size="small"
            onClick={() => {
              setOrderedKeys(ALL_COLUMNS.map((c) => c.key));
              setVisibleKeys(DEFAULT_VISIBLE);
            }}
          >
            ค่าเริ่มต้น
          </Button>
        }
      >
        <Typography.Paragraph type="secondary" className="!mb-3">
          ลากไอคอน <MenuOutlined /> เพื่อสลับลำดับ และติ๊กเพื่อซ่อน/แสดง — ลำดับนี้จะใช้ตอน Export ด้วย
        </Typography.Paragraph>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
            {orderedKeys.map((k) => {
              const c = ALL_COLUMNS.find((x) => x.key === k);
              if (!c) return null;
              return (
                <SortableColumnRow
                  key={k}
                  id={k}
                  title={c.title}
                  checked={visibleKeys.includes(k)}
                  onToggle={toggleColumn}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </Drawer>
    </div>
  );
}
